export const getCurrentUser = (): TUser | null => {
  try {
    const raw = localStorage.getItem("currentUser");
    if (!raw) return null;
    return JSON.parse(raw) as TUser;
  } catch {
    return null;
  }
};

export const getCurrentFleetId = (): number => {
  const currentUser = (getCurrentUser() ?? {}) as TUser;

  const companyUsers = currentUser?.company_users ?? [];

  // Prefer the FLEET_MANAGER entry (the fleet this user administrates),
  // then any entry that has a valid company_id. Never blindly trust [0]:
  // if that entry lacks company_id we would end up calling `fleets/0/...`.
  const fleetManager = companyUsers.find(
    (cu) => cu?.company_role === "FLEET_MANAGER" && Number(cu?.company_id) > 0
  );
  const anyWithCompany = companyUsers.find((cu) => Number(cu?.company_id) > 0);

  const companyId =
    fleetManager?.company_id ?? anyWithCompany?.company_id ?? 0;

  return Number(companyId) > 0 ? Number(companyId) : 0;
};

/**
 * The company_user entry that represents the current user inside the fleet
 * they administrate (preferring the FLEET_MANAGER role). Useful whenever we
 * need the user_id / department_id / company_role of the logged-in admin.
 */
export const getCurrentCompanyUser = (): TCompanyUser | null => {
  const currentUser = getCurrentUser();
  const companyUsers = currentUser?.company_users ?? [];
  const fleetManager = companyUsers.find(
    (cu) => cu?.company_role === "FLEET_MANAGER" && Number(cu?.company_id) > 0
  );
  return (
    fleetManager ??
    companyUsers.find((cu) => Number(cu?.company_id) > 0) ??
    null
  );
};

/** True when the logged-in user can administrate the current fleet. */
export const isFleetAdmin = (): boolean => {
  const currentUser = getCurrentUser();
  if (Number(currentUser?.is_super_admin) === 1) return true;
  return getCurrentCompanyUser()?.company_role === "FLEET_MANAGER";
};

/**
 * Persist a partial profile change back into the cached `currentUser` so the
 * UI (which reads from localStorage) reflects edits without a full reload.
 */
export const patchCurrentUser = (patch: Partial<TUser>): TUser | null => {
  const current = getCurrentUser();
  if (!current) return null;
  const next = { ...current, ...patch };
  localStorage.setItem("currentUser", JSON.stringify(next));
  return next;
};
